import React, { useState, useCallback } from "react";
import { AlertTriangle, ShieldCheck, Trash2 } from "lucide-react";

/** Small labeled form field wrapper used across modals. */
export function F({ label, children }) {
  return (
    <label className="block">
      <div className="label-caps text-white/50 text-[10px] mb-1">{label}</div>
      {children}
    </label>
  );
}

/** Rectangular placeholder shown while a tab is loading. */
export const Skeleton = () => (
  <div className="space-y-3">
    {[1, 2, 3, 4].map((i) => (
      <div key={i} className="h-20 rounded-2xl bg-white/5 animate-pulse" />
    ))}
  </div>
);

/** Global .nv-input styles used by New Vehicle / Event / Invoice modals. */
export const NvInputStyles = () => (
  <style>{`
    .nv-input {
      background: #0f0f11; border: 1px solid rgba(255,255,255,0.08);
      color: #fff; border-radius: 10px; padding: 10px 12px;
      width: 100%; font-size: 13px;
    }
    .nv-input:focus { outline: none; border-color: #A3E635; }
  `}</style>
);

/**
 * useConfirm — Promise-based replacement for the browser `window.confirm()`.
 * Native confirm() is silently blocked on iOS Safari PWA / installed mode,
 * so anywhere we need a destructive-action gate we render an in-app modal
 * instead.
 *
 * Usage:
 *   const { confirm, dialog } = useConfirm();
 *   const ok = await confirm({ title, message, tone: "danger" });
 *   ...
 *   return <>{jsx}{dialog}</>;
 */
export function useConfirm() {
  const [state, setState] = useState(null);
  const confirm = useCallback((opts) =>
    new Promise((resolve) => {
      setState({
        title: "Are you sure?",
        message: "",
        meta: "",
        note: "",
        confirmLabel: "Confirm",
        cancelLabel: "Cancel",
        tone: "danger", // "danger" | "warning" | "info"
        ...opts,
        resolve,
      });
    }), []);
  const close = (ok) => {
    setState((s) => {
      if (s?.resolve) s.resolve(ok);
      return null;
    });
  };
  const dialog = state
    ? <ConfirmDialog {...state} onCancel={() => close(false)} onConfirm={() => close(true)} />
    : null;
  return { confirm, dialog };
}

function ConfirmDialog({
  title, message, meta, note, confirmLabel, cancelLabel, tone,
  onConfirm, onCancel,
}) {
  const tones = {
    danger:  { ring: "border-red-500/40",   iconBg: "bg-red-500/15 text-red-300",       btn: "bg-red-500 text-white",     Icon: Trash2 },
    warning: { ring: "border-amber-400/40", iconBg: "bg-amber-400/15 text-amber-300",   btn: "bg-amber-400 text-midnight", Icon: AlertTriangle },
    info:    { ring: "border-cyan/40",      iconBg: "bg-cyan/15 text-cyan",             btn: "bg-cyan text-midnight",     Icon: ShieldCheck },
  };
  const t = tones[tone] || tones.danger;
  const Icon = t.Icon;
  return (
    <div
      data-testid="confirm-dialog"
      onClick={onCancel}
      className="fixed inset-0 z-[70] bg-ink-950/85 flex items-center justify-center p-4">
      <div
        onClick={(e) => e.stopPropagation()}
        className={`w-full max-w-sm bg-ink-900 border rounded-3xl p-6 space-y-4 ${t.ring}`}>
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${t.iconBg}`}>
            <Icon size={18} />
          </div>
          <div>
            <div className="text-white font-medium">{title}</div>
            {message && <div className="text-xs text-white/60 mt-0.5">{message}</div>}
          </div>
        </div>
        {meta && (
          <div className="bg-ink-950/60 rounded-xl p-3 text-xs text-white break-words">{meta}</div>
        )}
        {note && (
          <div className="text-[11px] text-amber-300 bg-amber-400/10 border border-amber-400/30 rounded-xl p-3">
            {note}
          </div>
        )}
        <div className="flex gap-2 pt-1">
          <button
            type="button"
            data-testid="confirm-dialog-cancel"
            onClick={onCancel}
            className="flex-1 rounded-full py-2.5 text-xs label-caps border border-white/15 text-white/70 hover:text-white">
            {cancelLabel}
          </button>
          <button
            type="button"
            data-testid="confirm-dialog-confirm"
            onClick={onConfirm}
            className={`flex-[2] rounded-full py-2.5 text-xs label-caps hover:brightness-110 inline-flex items-center justify-center gap-1.5 ${t.btn}`}>
            <Icon size={12} /> {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
